import { z } from "zod";
import { projectUrl } from "../helpers/urls";

// ============================================================================
// Input metrics (from Rails)
// ============================================================================

export const trendSchema = z.object({
  direction: z.enum(["up", "down", "flat"]),
  percent: z.number(),
});
export type Trend = z.infer<typeof trendSchema>;

export const totalsSchema = z.object({
  leads: z.number(),
  page_views: z.number(),
  ctr: z.number().nullable(),
  cpl: z.number().nullable(),
  ctr_available: z.boolean(),
  cpl_available: z.boolean(),
  roas: z.number().nullable().optional(),
  roas_available: z.boolean().optional(),
  total_spend_dollars: z.number().nullable().optional(),
});
export type Totals = z.infer<typeof totalsSchema>;

export const campaignSummarySchema = z.object({
  uuid: z.string(),
  slug: z.string(),
  name: z.string(),
  total_leads: z.number().optional(),
  total_page_views: z.number().optional(),
  ctr: z.number().nullable().optional(),
  cpl: z.number().nullable().optional(),
  roas: z.number().nullable().optional(),
  spend_dollars: z.number().nullable().optional(),
  days_since_last_lead: z.number().nullable().optional(),
});
export type CampaignSummary = z.infer<typeof campaignSummarySchema>;

export const projectSummarySchema = z.object({
  uuid: z.string(),
  slug: z.string(),
  name: z.string(),
  total_leads: z.number(),
  total_page_views: z.number(),
  ctr: z.number().nullable(),
  cpl: z.number().nullable(),
  days_since_last_lead: z.number().nullable().optional(),
  roas: z.number().nullable().optional(),
  spend_dollars: z.number().nullable().optional(),
  campaigns: z.array(campaignSummarySchema).default([]),
});
export type ProjectSummary = z.infer<typeof projectSummarySchema>;

export const trendsSchema = z.object({
  leads_trend: trendSchema.nullable(),
  page_views_trend: trendSchema.nullable(),
  ctr_trend: trendSchema.nullable().optional(),
  cpl_trend: trendSchema.nullable().optional(),
});
export type Trends = z.infer<typeof trendsSchema>;

export const flagsSchema = z.object({
  has_stalled_project: z.boolean().optional(),
  has_high_performer: z.boolean().optional(),
  has_new_first_lead: z.boolean().optional(),
});
export type Flags = z.infer<typeof flagsSchema>;

export const metricsSchema = z.object({
  period: z.string().optional().default("Last 30 Days"),
  totals: totalsSchema,
  projects: z.array(projectSummarySchema),
  trends: trendsSchema,
  flags: flagsSchema.optional(),
});
export type Metrics = z.infer<typeof metricsSchema>;

// ============================================================================
// Output: discriminated union by target_type
// ============================================================================

export const sentimentSchema = z.enum(["positive", "negative", "neutral"]);
export type Sentiment = z.infer<typeof sentimentSchema>;

const projectActionType = z.enum(["refine_messaging", "review_landing_page", "view_leads"]);
const campaignActionType = z.enum([
  "review_ad_copy",
  "review_keywords",
  "review_selling_points",
  "adjust_budget",
  "pause_campaign",
]);
const accountActionType = z.enum(["start_project"]);

export const insightActionTypeSchema = z.union([
  projectActionType,
  campaignActionType,
  accountActionType,
]);
export type InsightActionType = z.infer<typeof insightActionTypeSchema>;

const baseIntentFields = {
  title: z.string().max(50).describe("Short title (5 words max)"),
  description: z.string().describe("2-3 sentence explanation with specific numbers"),
  sentiment: sentimentSchema,
};

export const projectInsightIntentSchema = z.object({
  ...baseIntentFields,
  target_type: z.literal("project"),
  project_slug: z.string(),
  action_type: projectActionType,
});

export const campaignInsightIntentSchema = z.object({
  ...baseIntentFields,
  target_type: z.literal("campaign"),
  project_slug: z.string(),
  campaign_slug: z.string(),
  action_type: campaignActionType,
});

export const accountInsightIntentSchema = z.object({
  ...baseIntentFields,
  target_type: z.literal("account"),
  action_type: accountActionType,
});

export const insightIntentSchema = z.discriminatedUnion("target_type", [
  projectInsightIntentSchema,
  campaignInsightIntentSchema,
  accountInsightIntentSchema,
]);
export type InsightIntent = z.infer<typeof insightIntentSchema>;
export type ProjectInsightIntent = z.infer<typeof projectInsightIntentSchema>;
export type CampaignInsightIntent = z.infer<typeof campaignInsightIntentSchema>;
export type AccountInsightIntent = z.infer<typeof accountInsightIntentSchema>;

export const insightIntentsArraySchema = z.array(insightIntentSchema).length(3);

// ============================================================================
// ACTION_REGISTRY: each action knows what target it routes to
// ============================================================================

type ProjectAction = {
  target: "project";
  label: string;
  urlBuilder: (projectUuid: string) => string;
};
type CampaignAction = {
  target: "campaign";
  label: string;
  urlBuilder: (campaignUuid: string) => string;
};
type AccountAction = {
  target: "account";
  label: string;
  urlBuilder: () => string;
};
export type Action = ProjectAction | CampaignAction | AccountAction;

const campaignUrl = (uuid: string, ...segments: string[]) =>
  `/campaigns/${uuid}${segments.length ? "/" + segments.join("/") : ""}`;

export const ACTION_REGISTRY: Record<InsightActionType, Action> = {
  // project-targeted
  refine_messaging:    { target: "project",  label: "Refine Messaging",     urlBuilder: (p) => projectUrl(p, "brainstorm") },
  review_landing_page: { target: "project",  label: "Review Landing Page",  urlBuilder: (p) => projectUrl(p, "website", "build") },
  view_leads:          { target: "project",  label: "View Leads",           urlBuilder: (p) => projectUrl(p, "leads") },

  // campaign-targeted
  review_ad_copy:        { target: "campaign", label: "Review Ad Copy",        urlBuilder: (c) => campaignUrl(c, "content") },
  review_keywords:       { target: "campaign", label: "Review Keywords",       urlBuilder: (c) => campaignUrl(c, "keywords") },
  review_selling_points: { target: "campaign", label: "Review Selling Points", urlBuilder: (c) => campaignUrl(c, "highlights") },
  adjust_budget:         { target: "campaign", label: "Adjust Budget",         urlBuilder: (c) => campaignUrl(c, "settings") },
  pause_campaign:        { target: "campaign", label: "Pause Campaign",        urlBuilder: (c) => campaignUrl(c, "settings") },

  // account-targeted
  start_project: { target: "account", label: "Start a Project", urlBuilder: () => "/projects/new" },
};

// ============================================================================
// Resolver: slug → UUID dispatch
// ============================================================================

export type ProjectsTree = Array<{
  slug: string;
  uuid: string;
  name: string;
  campaigns: Array<{ slug: string; uuid: string; name: string }>;
}>;

export const insightActionSchema = z.object({
  label: z.string(),
  url: z.string(),
});
export type InsightAction = z.infer<typeof insightActionSchema>;

export const insightSchema = z.object({
  title: z.string().max(50),
  description: z.string(),
  sentiment: sentimentSchema,
  target_type: z.enum(["project", "campaign", "account"]),
  project_uuid: z.string().nullable(),
  campaign_uuid: z.string().nullable(),
  action: insightActionSchema,
});
export type Insight = z.infer<typeof insightSchema>;

export const insightsArraySchema = z.array(insightSchema).length(3);
export type InsightsArray = z.infer<typeof insightsArraySchema>;

export const insightsOutputSchema = z.object({
  insights: insightsArraySchema,
  generated_at: z.string().datetime().optional(),
});
export type InsightsOutput = z.infer<typeof insightsOutputSchema>;

export const jsonSchema = z.object({
  insights: insightsArraySchema.optional(),
});

export function resolveInsightIntent(
  intent: InsightIntent,
  projects: ProjectsTree,
): Insight {
  const action = ACTION_REGISTRY[intent.action_type];

  switch (intent.target_type) {
    case "account": {
      if (action.target !== "account") {
        throw new Error(`Action ${intent.action_type} does not target account`);
      }
      return {
        title: intent.title,
        description: intent.description,
        sentiment: intent.sentiment,
        target_type: "account",
        project_uuid: null,
        campaign_uuid: null,
        action: { label: action.label, url: action.urlBuilder() },
      };
    }
    case "project": {
      if (action.target !== "project") {
        throw new Error(`Action ${intent.action_type} does not target project`);
      }
      const project = projects.find((p) => p.slug === intent.project_slug);
      if (!project) throw new Error(`Unknown project_slug: ${intent.project_slug}`);
      return {
        title: intent.title,
        description: intent.description,
        sentiment: intent.sentiment,
        target_type: "project",
        project_uuid: project.uuid,
        campaign_uuid: null,
        action: { label: action.label, url: action.urlBuilder(project.uuid) },
      };
    }
    case "campaign": {
      if (action.target !== "campaign") {
        throw new Error(`Action ${intent.action_type} does not target campaign`);
      }
      const project = projects.find((p) => p.slug === intent.project_slug);
      const campaign = project?.campaigns.find((c) => c.slug === intent.campaign_slug);
      if (!project || !campaign) {
        throw new Error(
          `Unknown campaign: ${intent.project_slug}/${intent.campaign_slug}`,
        );
      }
      return {
        title: intent.title,
        description: intent.description,
        sentiment: intent.sentiment,
        target_type: "campaign",
        project_uuid: project.uuid,
        campaign_uuid: campaign.uuid,
        action: { label: action.label, url: action.urlBuilder(campaign.uuid) },
      };
    }
  }
}
